/**
 * Styles for the API surfaces: the run-level API band on the overview, the
 * per-row API evidence in the grid, and the assertions section in the detail
 * page.
 *
 * Tokens are the report's own (`html-css.ts`); nothing here introduces a new
 * colour that is not already in the palette.
 */

export const CSS_API = `
/* ── Run-level type band ── */
.tband{display:grid;gap:16px;margin-bottom:20px}
.tband-3{grid-template-columns:1.1fr 1.1fr .9fr}
.tband-2{grid-template-columns:1fr 1fr}
.tcard{background:var(--bg-1);border:1px solid rgba(255,255,255,.06);border-radius:12px;padding:16px 20px;display:flex;flex-direction:column;gap:12px;min-width:0}
.tcard-h{font-size:10px;font-weight:600;color:var(--fg-2);letter-spacing:.06em;text-transform:uppercase;display:flex;align-items:center;gap:8px}
.tcard-h .tcard-sub{margin-left:auto;font-weight:500;letter-spacing:0;text-transform:none;font-size:10px}

/* Method distribution */
.mrow{display:flex;align-items:center;gap:10px}
.mrow-k{width:54px;font-size:11px;font-weight:700;font-family:var(--mono,ui-monospace,SFMono-Regular,Menlo,Consolas,monospace)}
.mrow-track{display:block;flex:1;height:6px;border-radius:999px;background:rgba(255,255,255,.06);overflow:hidden}
.mrow-fill{display:block;height:100%;border-radius:999px;min-width:3px}
.mrow-n{font-size:11px;color:var(--fg-1);width:34px;text-align:right}

/* Status distribution */
.sbar{display:flex;height:8px;gap:2px}
.sbar>span{border-radius:999px}
.sgrid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:8px 12px}
.scell{display:flex;align-items:center;gap:8px}
.spill{font-size:10px;font-weight:700;padding:1px 7px;border-radius:4px}
.scell-n{font-size:12px;font-weight:700}
.scell-n.is-zero{font-weight:400;color:var(--fg-2)}
.sp-2xx{background:rgba(34,197,94,.12);color:#22c55e}
.sp-3xx{background:rgba(96,165,250,.12);color:#60a5fa}
.sp-4xx{background:rgba(251,146,60,.12);color:#fb923c}
.sp-5xx{background:rgba(239,68,68,.12);color:#ef4444}
.sp-error{background:rgba(239,68,68,.12);color:#ef4444}

/* Big-number card (assertions, timings) */
.tbig{display:flex;align-items:baseline;gap:8px}
.tbig-n{font-size:28px;font-weight:800;line-height:1}
.tbig-l{font-size:11px;color:var(--fg-1)}
.tsplit{display:flex;gap:14px;flex-wrap:wrap}
.tsplit-i{display:flex;align-items:center;gap:6px;font-size:13px;font-weight:700}
.tsplit-ok{color:#22c55e}
.tsplit-bad{color:#ef4444}
.tpair{display:flex;gap:18px;flex-wrap:wrap}
.tpair-i{display:flex;flex-direction:column;gap:2px}
.tpair-n{font-size:17px;font-weight:700}
.tpair-n small{font-size:11px;font-weight:500;color:var(--fg-1)}
.tpair-l{font-size:10px;color:var(--fg-1)}

/* ── Row evidence in the grid ── */
.arow{display:flex;gap:5px;align-items:center;flex-shrink:0}
.achip{font-size:10px;font-weight:700;padding:1px 6px;border-radius:4px;font-family:var(--mono,ui-monospace,SFMono-Regular,Menlo,Consolas,monospace)}
.amark{width:18px;height:18px;border-radius:4px;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.amark-ok{background:rgba(34,197,94,.12);color:#22c55e}
.amark-bad{background:rgba(239,68,68,.12);color:#ef4444}

/* ── Assertions section (detail page) ── */
.apisec{border:1px solid var(--bd);border-radius:11px;background:var(--bg-1);overflow:hidden}
.apisec-head{display:flex;align-items:center;gap:10px;padding:12px 20px;border-bottom:1px solid rgba(255,255,255,.06)}
.apisec-title{font-size:12px;font-weight:700;letter-spacing:.04em;text-transform:uppercase}
.apisec-badge{font-size:10px;font-weight:700;padding:1px 7px;border-radius:4px}
.apisec-badge.ab-ok{background:rgba(34,197,94,.12);color:#22c55e}
.apisec-badge.ab-bad{background:rgba(239,68,68,.12);color:#ef4444}
.asec{display:flex;flex-direction:column}
.arow-item{display:flex;align-items:center;gap:12px;padding:10px 20px;border-bottom:1px solid rgba(255,255,255,.04)}
.arow-item:last-child{border-bottom:none}
.arow-item.is-failed{background:rgba(239,68,68,.04)}
.atype{font-size:9px;font-weight:700;padding:1px 6px;border-radius:999px;letter-spacing:.04em;text-transform:uppercase;flex-shrink:0;background:rgba(255,255,255,.04);color:var(--fg-1)}
.arow-item.is-failed .atype{background:rgba(239,68,68,.14);color:#ef4444}
.aexpr{font-size:11.5px;flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-family:var(--mono,ui-monospace,SFMono-Regular,Menlo,Consolas,monospace)}
.aloc{font-size:10px;color:var(--fg-2);flex-shrink:0;font-family:var(--mono,ui-monospace,SFMono-Regular,Menlo,Consolas,monospace)}
.adiff{display:flex;gap:22px;flex-wrap:wrap;padding:0 20px 12px 52px}
.adiff-i{display:flex;gap:8px;align-items:baseline}
.adiff-k{font-size:10px;color:var(--fg-2);width:58px}
.adiff-v{font-size:11px;font-family:var(--mono,ui-monospace,SFMono-Regular,Menlo,Consolas,monospace);word-break:break-all}
.adiff-exp{color:#2dd4a8}
.adiff-act{color:#ef4444}
.acall{font-size:10px;color:var(--fg-2);padding:0 20px 10px 52px;font-family:var(--mono,ui-monospace,SFMono-Regular,Menlo,Consolas,monospace)}

/* ── API call row: the two columns that used to render empty ── */
.ndt-assert{display:inline-flex;align-items:center;justify-content:center;width:100%;font-size:10px;font-weight:700}
.ndt-assert-ok{color:#22c55e}
.ndt-assert-bad{color:#ef4444}
.ndt-assert-none{color:var(--fg-2);font-weight:400}
.ndt-err-note{color:#ef4444;font-size:11px}

/* Trace pane — a link out, plus how to open the thing */
.trace-open{display:flex;flex-direction:column;gap:10px;padding:18px;background:var(--bg-2);border:1px solid var(--bd);border-radius:10px}
.trace-link{display:inline-flex;align-items:center;gap:8px;align-self:flex-start;font-size:12.5px;font-weight:600;color:#2dd4a8;text-decoration:none;border:1px solid rgba(3,183,156,.3);background:rgba(3,183,156,.08);padding:8px 14px;border-radius:8px}
.trace-link:hover{background:rgba(3,183,156,.14)}
.trace-hint{font-size:11.5px;color:var(--fg-1);line-height:1.6}
.trace-hint code{font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;font-size:11px;background:var(--bg-code);padding:1px 5px;border-radius:4px;color:var(--fg)}

/* Cookie table */
.ndt-headers-label{font-size:9px;font-weight:700;color:var(--fg-2);letter-spacing:.06em;text-transform:uppercase;padding:10px 14px 0}
.ck-tbl{display:grid;grid-template-columns:auto 1fr;gap:6px 16px;padding:12px 14px;font-size:11.5px}
.ck-k{color:var(--fg-1);font-family:var(--mono,ui-monospace,SFMono-Regular,Menlo,Consolas,monospace)}
.ck-v{color:var(--fg);word-break:break-all;font-family:var(--mono,ui-monospace,SFMono-Regular,Menlo,Consolas,monospace)}
.ck-attr{color:var(--fg-2);font-size:10px}

@media (max-width:1100px){
  .tband-3,.tband-2{grid-template-columns:1fr}
}
`;
